import { Check, Copy } from 'lucide-react';
import { ReactNode, useState } from 'react';

import { Button, ButtonProps } from '@/components/ui/button';
import { TypographyInlineCode } from '@/components/ui/typography';

import { cn } from '@/lib/utils';

export const CopyToClipboard = ({
  value,
  prefix,
  className,
  textClassName,
  iconClassName,
}: {
  value: string;
  prefix?: ReactNode;
  className?: string;
  textClassName?: string;
  iconClassName?: string;
}) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (e: any, text: string) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1200);
    } catch {
      // Do nothing
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      className={cn(
        'relative max-w-[300px] px-2 rounded-full shadow-none',
        className,
      )}
      onClick={(e) => copyToClipboard(e, value)}
    >
      {prefix}
      <TypographyInlineCode
        className={cn(
          'truncate bg-transparent text-muted-foreground',
          textClassName,
        )}
      >
        {value}
      </TypographyInlineCode>
      <span
        aria-hidden={copied}
        className={cn(
          'pl-1 pointer-events-none flex items-center gap-0.5 text-muted-foreground transition duration-300',
          copied && '-translate-y-1.5 opacity-0',
        )}
      >
        <Copy
          className={cn(
            'h-5 w-5 fill-zinc-500/20 stroke-zinc-500 transition-colors group-hover/button:stroke-zinc-400',
            iconClassName,
          )}
        />
      </span>
      <span
        aria-hidden={copied}
        className={cn(
          'pointer-events-none absolute right-2 flex items-center gap-0.5 text-muted-foreground transition duration-300',
          !copied && 'translate-y-1.5 opacity-0',
          iconClassName,
        )}
      >
        <Check className="h-5 w-5 fill-success/20 stroke-success transition-colors group-hover/button:stroke-success/70" />
      </span>
    </Button>
  );
};

export const CopyToClipboardWithValue = ({
  value,
  className,
  onCopy,
  variant = 'ghost',
}: {
  value: string;
  className?: string;
  onCopy?: () => void;
  variant?: ButtonProps['variant'];
}) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (e: any, text: string) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (onCopy) onCopy();
      setTimeout(() => {
        setCopied(false);
      }, 1200);
    } catch {
      // Do nothing
    }
  };

  return (
    <Button
      variant={variant}
      size="icon-sm"
      className={cn('group hover:bg-transparent', className)}
      onClick={(e) => copyToClipboard(e, value)}
    >
      <span
        aria-hidden={copied}
        className={cn(
          'pointer-events-none flex items-center gap-0.5 transition duration-300',
          copied && '-translate-y-1.5 opacity-0',
        )}
      >
        <Copy className="h-4 w-4" />
      </span>
      <span
        aria-hidden={copied}
        className={cn(
          'pointer-events-none absolute flex items-center gap-0.5 transition duration-300',
          !copied && 'translate-y-1.5 opacity-0',
        )}
      >
        <Check className="h-4 w-4 fill-success/20 stroke-success transition-colors group-hover/button:stroke-success/70" />
      </span>
    </Button>
  );
};
